import React, { useState, useEffect, useRef } from 'react';
import { PencilIcon, PaperAirplaneIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { LuCircleCheck } from 'react-icons/lu';
import { CopyIcon } from '../../../../../components/icons/CopyIcon';
import gmailSvg from '../../../../../assets/gmail.svg';
import outlookSvg from '../../../../../assets/outlook.svg';

/**
 * Strips streaming/JSON artifacts that can leak into email content during streaming.
 * Includes HTML span tags from GracefulStreamingMarkdown's chunk animation.
 */
const sanitizeRawContent = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  let text = String(raw);
  // Remove streaming animation spans (keep content, strip tags)
  text = text.replace(/<span[^>]*class="[^"]*streaming-token[^"]*"[^>]*>/gi, '');
  text = text.replace(/<\/span>/g, '');
  // Remove JSON/SSE protocol artifacts that appear as literal text
  text = text.replace(/\s*\{[^{}]*"chunk"[^{}]*\}\s*/gi, '');
  text = text.replace(/\s*\{[^{}]*"data"[^{}]*\}\s*/gi, '');
  text = text.replace(/\s*"data"\s*:\s*"[^"]*"\s*,?\s*/gi, '');
  text = text.replace(/<chunk[^>]*>|<\/chunk>/gi, '');
  text = text.replace(/<data[^>]*>|<\/data>/gi, '');
  text = text.replace(/data:\s*\{[^}]*\}\s*/g, '');
  text = text.replace(/^\s*data:\s*/gm, '');
  return text.trim();
};

/**
 * Parses email content from AI response.
 * Supports Source:, Subject:, To:, From: headers (any order), then body after blank line.
 * Format:
 *   Source: gmail  (optional - gmail | outlook)
 *   Subject: Re: Are you alive
 *   To: Marc <marc@example.com>
 *   From: me@company.com
 *
 *   Hi Marc,
 *   Body content...
 */
export const parseEmailContent = (rawContent) => {
  if (!rawContent || typeof rawContent !== 'string') {
    return { subject: '', to: '', from: '', body: '', source: null };
  }
  const text = sanitizeRawContent(rawContent);
  const result = { subject: '', to: '', from: '', body: '', source: null };

  const sourceMatch = text.match(/^Source:\s*(gmail|outlook)\s*$/im);
  const subjectMatch = text.match(/^Subject:\s*(.+?)(?=\n|$)/im);
  const toMatch = text.match(/^To:\s*(.+?)(?=\n|$)/im);
  const fromMatch = text.match(/^From:\s*(.+?)(?=\n|$)/im);

  if (sourceMatch) result.source = sourceMatch[1].toLowerCase();
  if (subjectMatch) result.subject = subjectMatch[1].trim();
  if (toMatch) result.to = toMatch[1].trim();
  if (fromMatch) result.from = fromMatch[1].trim();

  // Body: everything after the last header line (headers can be in any order)
  const headerRegex = /^(?:Source|Subject|To|From):\s*.+$/gim;
  let match;
  let lastEnd = 0;
  while ((match = headerRegex.exec(text)) !== null) {
    lastEnd = match.index + match[0].length;
  }
  const afterHeaders = text.slice(lastEnd).replace(/^\s*\n+/, '').trim();
  if (afterHeaders) result.body = afterHeaders;

  // Fallback: no headers, whole thing is body (e.g. "Hi Marc,\n\n...")
  if (!result.subject && !result.to && !result.from && text) {
    result.body = text;
  }
  return result;
};

/**
 * Returns true if parsed content is a full verbatim email (not a summary).
 * Requires Subject + (From OR To) to avoid showing the card for summaries.
 */
export const isFullEmail = (parsed) => {
  if (!parsed) return false;
  const hasSubject = Boolean(parsed.subject?.trim());
  const hasFromOrTo = Boolean(parsed.from?.trim() || parsed.to?.trim());
  return hasSubject && hasFromOrTo;
};

export const stableEmailKey = (raw) => {
  const parsed = parseEmailContent(raw);
  const ident = [parsed.subject, parsed.to, parsed.from].filter(Boolean).join('|');
  if (ident) {
    let h = 0;
    for (let i = 0; i < ident.length; i++) {
      h = ((h << 5) - h + ident.charCodeAt(i)) | 0;
    }
    return `email-draft-${(h >>> 0).toString(36)}`;
  }
  let h = 0;
  for (let i = 0; i < Math.min((raw || '').length, 150); i++) {
    h = ((h << 5) - h + (raw || '').charCodeAt(i)) | 0;
  }
  return `email-draft-${(h >>> 0).toString(36)}`;
};

/**
 * Resolves email source for logo display.
 * Priority: emailSource prop > Source: header in content > default gmail
 */
const resolveEmailSource = (emailSource, parsedSource) => {
  if (emailSource === 'gmail' || emailSource === 'outlook') return emailSource;
  if (parsedSource === 'gmail' || parsedSource === 'outlook') return parsedSource;
  return 'gmail'; // default
};

const normalizeTo = (str) => {
  if (!str || typeof str !== 'string') return '';
  const trimmed = str.trim();
  const match = trimmed.match(/<([^>]+)>/);
  return match ? match[1].trim() : trimmed;
};

const EmailDraftCard = ({ rawContent, darkMode = false, onEdit, onSend, emailSource = 'email', sendDraftLoading = false, sentDrafts = [] }) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const bodyRef = useRef(null);
  const copyTimeoutRef = useRef(null);

  const { subject, to, from, body, source } = parseEmailContent(rawContent);
  const resolvedSource = resolveEmailSource(emailSource, source);
  const logoSrc = resolvedSource === 'outlook' ? outlookSvg : gmailSvg;

  const toNorm = normalizeTo(to);
  const sentDraft = sentDrafts.find(d => normalizeTo(d.to) === toNorm && (d.subject || '').trim() === (subject || '').trim());
  const sentAt = sentDraft?.sentAt;

  const hasContent = subject || to || from || body;

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const handleCopy = () => {
    const lines = [];
    if (to) lines.push(`To: ${to}`);
    if (from) lines.push(`From: ${from}`);
    if (subject) lines.push(`Subject: ${subject}`);
    if (lines.length) lines.push('');
    lines.push(body || '');
    navigator.clipboard.writeText(lines.join('\n'));
    setIsCopied(true);
    copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 2500);
  };

  const handleSend = () => {
    if (onSend) {
      onSend({ to, from, subject, body, source: resolvedSource });
    }
  };

  const canSend = Boolean(onSend && to && subject && (resolvedSource === 'outlook' || resolvedSource === 'gmail') && !sendDraftLoading);
  const sendLabel = resolvedSource === 'outlook' ? 'Outlook' : resolvedSource === 'gmail' ? 'Gmail' : 'Email';

  const bodyLines = (body || '').split('\n').length;
  const isLongBody = bodyLines > 20;
  const displayBody = isLongBody && !isExpanded ? (body || '').split('\n').slice(0, 20).join('\n') : (body || '');
  // Split body into paragraphs (double newlines) for proper formatting
  const bodyParagraphs = (displayBody || '').split(/\n\s*\n/).filter(Boolean);

  if (!hasContent) return null;

  const cardBg = darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-50';
  const borderColor = darkMode ? 'border-[#333333]' : 'border-gray-200';

  return (
    <div
      className={`email-draft-card mt-3 w-full min-w-0 max-w-full overflow-hidden font-sans border ${cardBg} ${borderColor}`}
      style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
    >
      {/* Header: Logo + "Email Draft" + actions */}
      <div className={`flex items-center justify-between px-4 py-3 ${cardBg}`}>
        <div className="flex items-center gap-2">
          <img src={logoSrc} alt={resolvedSource} className="w-5 h-5 flex-shrink-0" />
          <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Email Draft
          </span>
        </div>
        <div className="flex items-center gap-1">
          {onEdit && !sentAt && (
            <button
              onClick={onEdit}
              className={`email-squircle-btn-sm p-2 transition-colors ${
                darkMode ? 'hover:bg-[#2a2a2a] text-gray-400' : 'hover:bg-gray-200 text-gray-500'
              }`}
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleCopy}
            className={`email-squircle-btn-sm p-2 transition-colors ${
              darkMode ? 'hover:bg-[#2a2a2a] text-gray-400' : 'hover:bg-gray-200 text-gray-500'
            }`}
            title="Copy"
          >
            {isCopied ? (
              <LuCircleCheck className={`w-4 h-4 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
            ) : (
              <CopyIcon size={16} className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
            )}
          </button>
          {sentAt ? (
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${darkMode ? 'bg-[#2a2a2a] text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
              <LuCircleCheck className={`w-4 h-4 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
              Sent at {new Date(sentAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </span>
          ) : (
            <button
              onClick={handleSend}
              disabled={!canSend}
              className={`email-squircle-btn flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                canSend
                  ? darkMode
                    ? 'bg-white text-black hover:bg-gray-200'
                    : 'bg-black text-white hover:bg-gray-800'
                  : darkMode
                    ? 'bg-[#2a2a2a] text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              title={canSend ? `Send with ${sendLabel}` : 'Connect Outlook or Gmail to send'}
            >
              <PaperAirplaneIcon className={`w-4 h-4 ${sendDraftLoading ? 'animate-pulse' : ''}`} />
              <span>{sendDraftLoading ? 'Sending...' : `Send with ${sendLabel}`}</span>
            </button>
          )}
        </div>
      </div>

      {/* Separator with margin */}
      <div className={`mx-4 border-t ${borderColor}`} />

      {/* Content: Subject first, then Receivers, divider, body */}
      <div className="px-4 py-4 space-y-3">
        {subject && (
          <div className="min-w-0 break-words">
            <p className={`text-base font-semibold break-words ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {subject}
            </p>
          </div>
        )}
        {to && (
          <div className="min-w-0 break-words">
            <span className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              To
            </span>
            <p className={`text-sm mt-0.5 break-words ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{to}</p>
          </div>
        )}
        {from && (
          <div className="min-w-0 break-words">
            <span className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>From</span>
            <p className={`text-sm mt-0.5 break-words ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{from}</p>
          </div>
        )}
        {(subject || to || from) && body && (
          <hr className={`border-0 border-t ${darkMode ? 'border-[#333333]' : 'border-gray-200'} my-3`} />
        )}
        {body && (
          <div className="email-draft-body min-w-0 break-words" style={{ fontFamily: 'var(--chat-font-family, system-ui, -apple-system, sans-serif)' }}>
            <div
              ref={bodyRef}
              className={`text-sm leading-relaxed break-words ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
              style={{ maxHeight: isLongBody && !isExpanded ? '24rem' : 'none', overflow: 'hidden', overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
            >
              {bodyParagraphs.length > 0 ? (
                bodyParagraphs.map((para, i) => (
                  <p key={i} className="mb-3 last:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    {para}
                  </p>
                ))
              ) : (
                <p style={{ whiteSpace: 'pre-wrap' }}>{displayBody}</p>
              )}
            </div>
            {isLongBody && (
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                    darkMode
                      ? 'bg-[#2a2a2a] text-gray-400 hover:text-white hover:bg-[#333333]'
                      : 'bg-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-300'
                  }`}
                  title={isExpanded ? 'Show less' : 'Show more'}
                >
                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailDraftCard;
